import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Copy, Trash2, TrendingUp, TrendingDown, DollarSign, CheckCircle2, ArrowRight, ChevronDown, Coins, Clock, BarChart3, PiggyBank } from 'lucide-react';
import CryptoAffiliateBlock from '../../CryptoAffiliateBlock';

const BitcoinRoiCalculatorTool = () => {
  const [initialInvestment, setInitialInvestment] = useState('');
  const [currentValue, setCurrentValue] = useState('');
  const [duration, setDuration] = useState('');
  const [durationUnit, setDurationUnit] = useState('months');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');
  const [showCoinMenu, setShowCoinMenu] = useState(false);

  useEffect(() => {
    calculate();
  }, [initialInvestment, currentValue, duration, durationUnit]);

  const calculate = () => {
    const initial = parseFloat(initialInvestment);
    const current = parseFloat(currentValue);
    const dur = parseFloat(duration) || 0;

    if (isNaN(initial) || isNaN(current) || initial <= 0) {
      setResult(null);
      return;
    }

    const profit = current - initial;
    const roi = ((current - initial) / initial) * 100;
    const growthMultiplier = current / initial;

    let durationInYears = dur;
    if (durationUnit === 'days') {
      durationInYears = dur / 365;
    } else if (durationUnit === 'months') {
      durationInYears = dur / 12;
    }

    let annualizedRoi = 0;
    if (durationInYears > 0 && initial > 0 && current > 0) {
      annualizedRoi = (Math.pow(current / initial, 1 / durationInYears) - 1) * 100;
    }

    const dailyReturn = durationInYears > 0 ? roi / (durationInYears * 365) : 0;
    const monthlyReturn = durationInYears > 0 ? roi / (durationInYears * 12) : 0;

    setResult({
      profit,
      roi,
      annualizedRoi,
      growthMultiplier,
      dailyReturn,
      monthlyReturn,
      durationInYears,
      isProfitable: profit > 0,
      isBreakEven: Math.abs(profit) < 0.01
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(2) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setInitialInvestment('');
    setCurrentValue('');
    setDuration('');
    setResult(null);
  };

  const formatCurrency = (value) => '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatPercent = (value) => (value >= 0 ? '+' : '') + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

  const scenarios = [
    { label: '$10K → $25K (1yr)', initial: '10000', current: '25000', dur: '12', unit: 'months' },
    { label: '$5K → $50K (2yr)', initial: '5000', current: '50000', dur: '2', unit: 'years' },
    { label: '$1K → $10K (6mo)', initial: '1000', current: '10000', dur: '6', unit: 'months' },
  ];

  const applyScenario = (scenario) => {
    setInitialInvestment(scenario.initial);
    setCurrentValue(scenario.current);
    setDuration(scenario.dur);
    setDurationUnit(scenario.unit);
  };

  const coinOptions = [
    { name: 'Bitcoin (BTC)', slug: 'bitcoin-roi-calculator', icon: '₿', current: true },
    { name: 'Ethereum (ETH)', slug: 'ethereum-roi-calculator', icon: 'Ξ', current: false },
    { name: 'All Cryptocurrencies', slug: 'crypto-roi-calculator', icon: '🪙', current: false },
  ];

  const getRoiRating = (roi) => {
    if (roi >= 100) return { label: 'Exceptional', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (roi >= 50) return { label: 'Excellent', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (roi >= 20) return { label: 'Good', color: 'text-blue-400', bg: 'bg-blue-500/20' };
    if (roi >= 0) return { label: 'Positive', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (roi >= -20) return { label: 'Minor Loss', color: 'text-orange-400', bg: 'bg-orange-500/20' };
    return { label: 'Significant Loss', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  return (
    <div className="space-y-8" data-testid="bitcoin-roi-calculator">
      {/* Coin Selector Dropdown */}
      <div className="relative">
        <button
          onClick={() => setShowCoinMenu(!showCoinMenu)}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-lg text-orange-400 hover:bg-orange-500/30 transition-colors"
          data-testid="coin-selector"
        >
          <span className="text-xl">₿</span>
          <span className="font-medium">Bitcoin ROI Calculator</span>
          <ChevronDown size={16} className={`transition-transform ${showCoinMenu ? 'rotate-180' : ''}`} />
        </button>
        
        {showCoinMenu && (
          <div className="absolute top-full left-0 mt-2 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
            {coinOptions.map((coin) => (
              coin.current ? (
                <div key={coin.slug} className="flex items-center gap-3 px-4 py-3 bg-orange-500/10 text-orange-400 cursor-default">
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <CheckCircle2 size={16} className="ml-auto" />
                </div>
              ) : (
                <Link
                  key={coin.slug}
                  to={`/tools/${coin.slug}`}
                  className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 transition-colors"
                  onClick={() => setShowCoinMenu(false)}
                >
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <ArrowRight size={16} className="ml-auto opacity-50" />
                </Link>
              )
            ))}
          </div>
        )}
      </div>

      {/* Quick Examples */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-400 mr-2">Quick Examples:</span>
        {scenarios.map((scenario, index) => (
          <button
            key={index}
            onClick={() => applyScenario(scenario)}
            className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-gray-300 transition-colors"
            data-testid={`scenario-${index}`}
          >
            {scenario.label}
          </button>
        ))}
      </div>

      {/* Calculator Input Section */}
      <div className="p-6 bg-gray-800 rounded-xl space-y-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
            <BarChart3 size={22} className="text-orange-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Bitcoin ROI Calculator</h3>
            <p className="text-sm text-gray-400">Calculate your BTC return on investment</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Initial Investment (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={initialInvestment}
                onChange={(e) => setInitialInvestment(e.target.value)}
                placeholder="e.g., 10000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none text-lg"
                data-testid="initial-investment-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Amount originally invested in BTC</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Current Value (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={currentValue}
                onChange={(e) => setCurrentValue(e.target.value)}
                placeholder="e.g., 25000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none text-lg"
                data-testid="current-value-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Current value of your BTC holdings</p>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Investment Duration <span className="text-gray-500">(for annualized ROI)</span>
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                step="1"
                min="0"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="e.g., 12"
                className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                data-testid="duration-input"
              />
              <select
                value={durationUnit}
                onChange={(e) => setDurationUnit(e.target.value)}
                className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-orange-500 focus:outline-none"
                data-testid="duration-unit-select"
              >
                <option value="days">Days</option>
                <option value="months">Months</option>
                <option value="years">Years</option>
              </select>
            </div>
            <p className="text-xs text-gray-500 mt-1">Required for CAGR calculation</p>
          </div>
        </div>

        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors text-sm"
          data-testid="clear-btn"
        >
          <Trash2 size={14} />
          Clear All
        </button>
      </div>

      {/* Results Section */}
      {result && (
        <div className="space-y-4">
          {/* Main Result - ROI */}
          <div className={`p-6 rounded-xl border-2 ${result.isProfitable ? 'bg-green-500/10 border-green-500/40' : result.isBreakEven ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-red-500/10 border-red-500/40'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {result.isProfitable ? (
                  <TrendingUp className="text-green-400" size={28} />
                ) : result.isBreakEven ? (
                  <DollarSign className="text-yellow-400" size={28} />
                ) : (
                  <TrendingDown className="text-red-400" size={28} />
                )}
                <span className="text-lg font-medium text-gray-200">Bitcoin ROI</span>
              </div>
              <button
                onClick={() => copyResult(result.roi, 'roi')}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                {copied === 'roi' ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} className="text-gray-400" />}
              </button>
            </div>
            <div className="text-center">
              <p className={`text-5xl font-bold ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {formatPercent(result.roi)}
              </p>
              <div className="mt-2">
                <span className={`inline-block px-3 py-1 rounded-full text-sm ${getRoiRating(result.roi).bg} ${getRoiRating(result.roi).color}`}>
                  {getRoiRating(result.roi).label}
                </span>
              </div>
              <p className="text-gray-400 mt-2">
                Your Bitcoin grew <span className="text-white font-semibold">{result.growthMultiplier.toFixed(2)}x</span>
              </p>
            </div>
          </div>

          {/* Detailed Results Grid */}
          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Total Profit</p>
              <p className={`text-xl font-semibold ${result.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {result.profit >= 0 ? '+' : ''}{formatCurrency(result.profit)}
              </p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Annualized ROI (CAGR)</p>
              <p className={`text-xl font-semibold ${result.annualizedRoi >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                {result.durationInYears > 0 ? formatPercent(result.annualizedRoi) : 'N/A'}
              </p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Initial Investment</p>
              <p className="text-xl font-semibold text-white">{formatCurrency(parseFloat(initialInvestment) || 0)}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Current Value</p>
              <p className="text-xl font-semibold text-white">{formatCurrency(parseFloat(currentValue) || 0)}</p>
            </div>
          </div>

          {/* Period Returns */}
          {result.durationInYears > 0 && (
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                <Clock size={16} className="text-orange-400" />
                Average Returns by Period
              </h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-gray-400 text-xs mb-1">Daily</p>
                  <p className={`font-semibold ${result.dailyReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatPercent(result.dailyReturn)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Monthly</p>
                  <p className={`font-semibold ${result.monthlyReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatPercent(result.monthlyReturn)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Yearly (CAGR)</p>
                  <p className={`font-semibold ${result.annualizedRoi >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                    {formatPercent(result.annualizedRoi)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Calculation Breakdown */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
              <Calculator size={16} className="text-orange-400" />
              Calculation Breakdown
            </h4>
            <div className="space-y-2 text-sm font-mono">
              <p className="text-gray-400">
                ROI = ({formatCurrency(parseFloat(currentValue) || 0)} - {formatCurrency(parseFloat(initialInvestment) || 0)}) / {formatCurrency(parseFloat(initialInvestment) || 0)} × 100 = <span className={result.roi >= 0 ? 'text-green-400' : 'text-red-400'}>{formatPercent(result.roi)}</span>
              </p>
              <p className="text-gray-400">
                Profit = {formatCurrency(parseFloat(currentValue) || 0)} - {formatCurrency(parseFloat(initialInvestment) || 0)} = <span className={result.profit >= 0 ? 'text-green-400' : 'text-red-400'}>{result.profit >= 0 ? '+' : ''}{formatCurrency(result.profit)}</span>
              </p>
              {result.durationInYears > 0 && (
                <p className="text-gray-400">
                  CAGR = ({formatCurrency(parseFloat(currentValue) || 0)} / {formatCurrency(parseFloat(initialInvestment) || 0)})^(1/{result.durationInYears.toFixed(2)}) - 1 = <span className="text-blue-400">{formatPercent(result.annualizedRoi)}</span>
                </p>
              )}
            </div>
          </div>
          
          {/* Affiliate CTA Block */}
          <CryptoAffiliateBlock 
            profit={result.profit} 
            cryptoName="BTC"
            toolName="Bitcoin ROI Calculator"
            toolSlug="bitcoin-roi-calculator"
          />
        </div>
      )}

      {/* Related Crypto Tools - Internal Links */}
      <div className="p-5 bg-gradient-to-r from-orange-500/10 to-yellow-500/10 rounded-xl border border-orange-500/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Coins size={20} className="text-orange-400" />
          Related Bitcoin & Crypto Tools
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link
            to="/tools/bitcoin-profit-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-orange-400 transition-colors">BTC Profit</p>
              <p className="text-sm text-gray-400">Calculate profit</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-orange-400 transition-colors" />
          </Link>
          <Link
            to="/tools/ethereum-roi-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-indigo-400 transition-colors">ETH ROI</p>
              <p className="text-sm text-gray-400">Ethereum returns</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-indigo-400 transition-colors" />
          </Link>
          <Link
            to="/tools/crypto-investment-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-green-400 transition-colors">Investment</p>
              <p className="text-sm text-gray-400">Plan investments</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-green-400 transition-colors" />
          </Link>
          <Link
            to="/tools/crypto-tax-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-purple-400 transition-colors">Crypto Tax</p>
              <p className="text-sm text-gray-400">Estimate taxes</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-purple-400 transition-colors" />
          </Link>
        </div>
      </div>

      {/* Formula Reference */}
      <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
          <Calculator size={16} className="text-purple-400" />
          Bitcoin ROI Formulas
        </h4>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="bg-gray-900 p-3 rounded-lg">
            <p className="text-gray-400 mb-1">Basic ROI:</p>
            <code className="text-purple-400">((Current - Initial) / Initial) × 100</code>
          </div>
          <div className="bg-gray-900 p-3 rounded-lg">
            <p className="text-gray-400 mb-1">Annualized ROI (CAGR):</p>
            <code className="text-purple-400">(Current/Initial)^(1/years) - 1</code>
          </div>
        </div>
      </div>

      {/* Tips Section */}
      <div className="p-5 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/20">
        <h4 className="text-lg font-semibold text-white mb-3">💡 Bitcoin ROI Insights</h4>
        <ul className="space-y-2 text-gray-300">
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Historical Context:</strong> Bitcoin has averaged ~200% annual ROI since 2010, but with extreme volatility</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Halving Cycles:</strong> BTC historically shows strong ROI in the 12-18 months following halving events</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>CAGR vs Simple ROI:</strong> Use annualized ROI to compare investments of different durations fairly</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Unrealized vs Realized:</strong> Current value shows unrealized gains—actual ROI depends on when you sell</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Include All Costs:</strong> Factor in exchange fees, network fees, and taxes for true ROI</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default BitcoinRoiCalculatorTool;
